import PlotWrapper from "../components/PlotWrapper";
import { PLOT_CONFIG, PLOT_LAYOUT } from "../constants";
import React, { useEffect, useLayoutEffect, useState } from "react";
import { TRootState } from "../store/create-store";
import { useDispatch, useSelector } from "react-redux";
import { calculateScatterPlotDimensions } from "../utils/window-utils";
import { Figure } from "react-plotly.js";
import debounce from "lodash.debounce";
import useWindowResize from "../hooks/useWindowResize";
import { PlotMouseEvent } from "plotly.js";
import { rootActions } from "../reducers/root.slice";
import { setStatus } from "../dom-utils";
import { selectRightPanelState, selectRootState } from "../selectors";
import { deepClone } from "../utils/utils";
import MathUtils from "../utils/MathUtils";
import { selectPCAForSelectedMethod } from "../selectors/index.selector";

const ScatterPlot = () => {
  const dispatch = useDispatch();
  const {
    graphData,
    rightPanelVisible,
    showAxes,
    camera,
    xAxisTitle,
    yAxisTitle,
    zAxisTitle,
  } = useSelector(selectScatterPlot);

  const [graphLayout, setGraphLayout] = useState<Figure["layout"]>({
    ...PLOT_LAYOUT,
    ...calculateScatterPlotDimensions(rightPanelVisible),
  });

  useEffect(() => {
    setGraphLayout({
      ...graphLayout,
      scene: {
        ...graphLayout.scene,
        xaxis: {
          ...graphLayout.scene?.xaxis,
          title: xAxisTitle,
        },
        yaxis: {
          ...graphLayout.scene?.yaxis,
          title: yAxisTitle,
        },
        zaxis: {
          ...graphLayout.scene?.zaxis,
          title: zAxisTitle,
        },
      },
    });
  }, [xAxisTitle]);

  function handleResize() {
    const dimensions = calculateScatterPlotDimensions(rightPanelVisible);

    setGraphLayout({
      ...graphLayout,
      ...dimensions,
    });
  }

  const debouncedHandleResize = debounce(handleResize, 100);
  useWindowResize(debouncedHandleResize);

  const onPointClick = (event: PlotMouseEvent) => {
    // TODO handle multiple points
    console.info(`Clicked on ${event.points.length} points`);
    // TODO type this
    dispatch(rootActions.findClickedPoint((event.points[0] as any).text));
  };

  useLayoutEffect(debouncedHandleResize, [rightPanelVisible]);

  useLayoutEffect(() => {
    if (
      !graphLayout.scene ||
      !graphLayout.scene.xaxis ||
      !graphLayout.scene.yaxis ||
      !graphLayout.scene.zaxis
    ) {
      return;
    }

    graphLayout.scene.xaxis.visible = showAxes;
    graphLayout.scene.yaxis.visible = showAxes;
    graphLayout.scene.zaxis.visible = showAxes;
    setGraphLayout({ ...graphLayout });
  }, [showAxes]);

  useLayoutEffect(() => {
    if (graphLayout.scene && camera) {
      graphLayout.scene.camera = deepClone(camera);
      setGraphLayout({ ...graphLayout });
    }
  }, [camera]);

  const onRelayout = (x: number, y: number, z: number) => {
    // console.log(x * 57.2958, y * 57.2958, z * 57.2958);
    // dispatch(rootActions.rotateHeatMap({ x, y, z }));
  };
  const debouncedOnRelayout = debounce(onRelayout, 100);

  return (
    <PlotWrapper
      data={graphData}
      layout={graphLayout}
      config={PLOT_CONFIG}
      onClick={onPointClick}
      onRelayout={(layout) => {
        // if (
        //   !isCameraPreset(layout.scene?.camera, CAMERA_XZ) &&
        //   !isCameraPreset(layout.scene?.camera, CAMERA_YZ) &&
        //   !isCameraPreset(layout.scene?.camera, CAMERA_XY)
        // ) {
        //   dispatch(
        //     rootActions.setRightPanel({
        //       camera: undefined,
        //     })
        //   );
        // }

        const camera = layout.scene?.camera || (layout as any)["scene.camera"];
        if (camera) {
          debouncedOnRelayout(
            camera.eye?.x || 1.25,
            camera.eye?.y || 1.25,
            camera.eye?.z || 1.25
          );
        }
      }}
      onAfterPlot={() => {
        let traces = 0;
        let totalPoints = 0;
        graphData.forEach((trace) => {
          traces += 1;
          totalPoints += trace?.x?.length || 0;
        });
        setStatus(
          `Ready (${traces} trace${
            traces === 1 ? "" : "s"
          }, ${totalPoints} points)`
        );
      }}
    />
  );
};

const selectScatterPlot = (state: TRootState) => {
  const rightPanel = selectRightPanelState(state);

  let xAxisTitle = "x";
  let yAxisTitle = "y";
  let zAxisTitle = "z";
  const pca = selectPCAForSelectedMethod(state);

  if (rightPanel.projectToPCA && pca) {
    xAxisTitle = "PCA1 = " + MathUtils.percentage(pca.x) + "%";
    yAxisTitle = "PCA2 = " + MathUtils.percentage(pca.y) + "%";
    zAxisTitle = "PCA3 = " + MathUtils.percentage(pca.z) + "%";
  }

  return {
    graphData: selectRootState(state).graphData,
    rightPanelVisible: rightPanel.visible,
    showAxes: rightPanel.showAxes,
    camera: rightPanel.camera,
    xAxisTitle,
    yAxisTitle,
    zAxisTitle,
  };
};

export default ScatterPlot;
